import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSend, FiCheck, FiAlertCircle, FiMail, FiMessageSquare, FiUser, FiGlobe } = FiIcons;

const ReportForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    abuseType: '',
    description: '',
    evidence: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const abuseTypes = [
    'Spam E-Mail',
    'Phishing',
    'Malware',
    'Urheberrechtsverletzung',
    'Betrug',
    'Andere'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Create mailto link with all form data
      const subject = encodeURIComponent(`Abuse Meldung: ${formData.abuseType} von ${formData.name}`);
      const body = encodeURIComponent(
        `Abuse Meldung\n\n` +
        `Name: ${formData.name}\n` +
        `E-Mail: ${formData.email}\n` +
        `Art des Missbrauchs: ${formData.abuseType}\n\n` +
        `Beschreibung:\n${formData.description}\n\n` +
        `Beweise/URLs:\n${formData.evidence}\n\n` +
        `---\n` +
        `Gesendet am: ${new Date().toLocaleString('de-DE')}\n` +
        `IP-Adresse: [wird vom Server erfasst]`
      );

      // Open email client
      window.location.href = `mailto:abuse@malerbetrieb-pelzer.de?subject=${subject}&body=${body}`;

      // Small delay to ensure email client opens
      await new Promise(resolve => setTimeout(resolve, 1000));

      setIsSubmitted(true);
    } catch (err) {
      setError('Beim Senden der E-Mail ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (isSubmitted) {
    return (
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-2xl">
          <motion.div 
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-md border border-green-500/30 rounded-3xl p-12 text-center"
          >
            <motion.div 
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
              className="bg-gradient-to-r from-green-500 to-emerald-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <SafeIcon icon={FiCheck} className="w-10 h-10 text-white" />
            </motion.div>
            <h2 className="text-3xl font-bold text-white mb-4">E-Mail geöffnet!</h2>
            <p className="text-gray-300 text-lg mb-4">
              Ihr Standard-E-Mail-Programm wurde geöffnet mit allen Informationen.
            </p>
            <p className="text-gray-300 text-sm">
              Bitte senden Sie die E-Mail an <span className="text-cyan-400 font-medium">abuse@malerbetrieb-pelzer.de</span>, 
              um Ihre Meldung abzuschließen.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 px-6">
      <div className="container mx-auto max-w-4xl">
        <motion.div 
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="bg-black/30 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-12"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent mb-4">
              Missbrauch Melden
            </h2>
            <p className="text-gray-300 text-lg">
              Füllen Sie das Formular aus - Ihre Daten werden direkt an abuse@malerbetrieb-pelzer.de gesendet
            </p>
          </div>

          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-300 text-sm"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid md:grid-cols-2 gap-8">
              <motion.div whileFocus={{ scale: 1.02 }} transition={{ type: "spring", stiffness: 300 }}>
                <label className="block text-gray-300 font-medium mb-3 flex items-center space-x-2">
                  <SafeIcon icon={FiUser} className="w-5 h-5 text-cyan-400" />
                  <span>Name</span>
                </label>
                <input 
                  type="text" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  required 
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:bg-white/10 transition-all duration-300"
                  placeholder="Ihr vollständiger Name"
                />
              </motion.div>

              <motion.div whileFocus={{ scale: 1.02 }} transition={{ type: "spring", stiffness: 300 }}>
                <label className="block text-gray-300 font-medium mb-3 flex items-center space-x-2">
                  <SafeIcon icon={FiMail} className="w-5 h-5 text-cyan-400" />
                  <span>E-Mail</span>
                </label>
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:bg-white/10 transition-all duration-300"
                  placeholder="ihre.email@beispiel.de"
                />
              </motion.div>
            </div>

            <motion.div whileFocus={{ scale: 1.02 }} transition={{ type: "spring", stiffness: 300 }}>
              <label className="block text-gray-300 font-medium mb-3 flex items-center space-x-2">
                <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-orange-400" />
                <span>Art des Missbrauchs</span>
              </label>
              <select 
                name="abuseType" 
                value={formData.abuseType} 
                onChange={handleChange} 
                required
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white focus:outline-none focus:border-orange-400 focus:bg-white/10 transition-all duration-300"
              >
                <option value="">Bitte wählen Sie eine Kategorie</option>
                {abuseTypes.map((type, index) => (
                  <option key={index} value={type} className="bg-gray-800">
                    {type}
                  </option>
                ))}
              </select>
            </motion.div>

            <motion.div whileFocus={{ scale: 1.02 }} transition={{ type: "spring", stiffness: 300 }}>
              <label className="block text-gray-300 font-medium mb-3 flex items-center space-x-2">
                <SafeIcon icon={FiMessageSquare} className="w-5 h-5 text-purple-400" />
                <span>Beschreibung</span>
              </label>
              <textarea 
                name="description" 
                value={formData.description} 
                onChange={handleChange} 
                required 
                rows={6}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:bg-white/10 transition-all duration-300 resize-none"
                placeholder="Beschreiben Sie den Vorfall detailliert. Je mehr Informationen Sie bereitstellen, desto besser können wir Ihnen helfen."
              />
            </motion.div>

            <motion.div whileFocus={{ scale: 1.02 }} transition={{ type: "spring", stiffness: 300 }}>
              <label className="block text-gray-300 font-medium mb-3 flex items-center space-x-2">
                <SafeIcon icon={FiGlobe} className="w-5 h-5 text-green-400" />
                <span>Beweise / URLs</span>
              </label>
              <textarea 
                name="evidence" 
                value={formData.evidence} 
                onChange={handleChange} 
                rows={4}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white placeholder-gray-400 focus:outline-none focus:border-green-400 focus:bg-white/10 transition-all duration-300 resize-none"
                placeholder="Links, E-Mail-Header oder andere Beweise (optional)"
              />
            </motion.div>

            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 text-cyan-300 text-sm">
              <p className="flex items-center space-x-2">
                <SafeIcon icon={FiMail} className="w-4 h-4" />
                <span>
                  Nach dem Klick auf "Bericht Senden" öffnet sich Ihr E-Mail-Programm mit allen ausgefüllten Informationen. 
                  Sie müssen nur noch auf "Senden" klicken.
                </span>
              </p>
            </div>

            <motion.button 
              type="submit" 
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <motion.div 
                    animate={{ rotate: 360 }} 
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                  />
                  <span>Wird gesendet...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSend} className="w-6 h-6" />
                  <span>Bericht Senden</span>
                </>
              )}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default ReportForm;